/**
 * The single render/serialize adapter for the campaign content contract.
 *
 * Backend contract v2 (see `_campaign.json.jbuilder` / `campaign_params`):
 *
 *   campaign.content = {
 *     headline, body, image_url, image_alt, theme_color,
 *     ctas: [{ type, label, url, phone, copy_text }]
 *   }
 *
 * — max five CTAs. Per-type fields: `url` is required for `open_link` and
 * `page_snippet`, `phone` for `phone_call`, `copy_text` for `copy_text`;
 * every other combination is null. The legacy `start_conversation` type is
 * normalized to `send_message` server-side; we mirror that here so stale
 * store data renders correctly.
 *
 * `image_url` may also hold a curated-library token (`preset:<name>`) that
 * renderers resolve to a CSS gradient — see
 * `shared/helpers/campaignImagePresets.js` for the scheme.
 *
 * `theme_color` is a `#rrggbb` string driving the widget card's header and
 * primary CTA; empty string means "use the product default".
 *
 * Campaigns created before the builder have no `content`; they fall back to
 * the legacy fields (`headline` ← `title`, `body` ← `message`) so every
 * campaign renders in the builder preview. The fallback is per-field with
 * `??` on purpose: a content object with explicitly emptied strings must not
 * resurrect the legacy values.
 */

export const CTA_TYPES = {
  SEND_MESSAGE: 'send_message',
  PHONE_CALL: 'phone_call',
  OPEN_LINK: 'open_link',
  PAGE_SNIPPET: 'page_snippet',
  COPY_TEXT: 'copy_text',
  DISMISS: 'dismiss',
};

export const MAX_CTAS = 5;

/** CTA types whose action needs a `url`. */
export const CTA_URL_TYPES = [CTA_TYPES.OPEN_LINK, CTA_TYPES.PAGE_SNIPPET];

export const DEFAULT_THEME_COLOR = '#6e56cf';

/** Swatches offered in the builder's Design tab; first is the default. */
export const THEME_COLOR_PRESETS = [
  '#6e56cf',
  '#2563eb',
  '#0d9488',
  '#dc2626',
  '#d97706',
  '#1f2937',
];

export const isValidThemeColor = value => /^#[0-9a-fA-F]{6}$/.test(value ?? '');

const CTA_TYPE_VALUES = Object.values(CTA_TYPES);

/** Server-side alias kept for pre-v2 rows. */
const LEGACY_CTA_TYPE_ALIASES = { start_conversation: CTA_TYPES.SEND_MESSAGE };

const normalizeCta = cta => {
  const rawType = LEGACY_CTA_TYPE_ALIASES[cta?.type] ?? cta?.type;
  const type = CTA_TYPE_VALUES.includes(rawType)
    ? rawType
    : CTA_TYPES.OPEN_LINK;
  return {
    type,
    label: cta?.label ?? '',
    url: CTA_URL_TYPES.includes(type) ? (cta?.url ?? '') : '',
    phone: type === CTA_TYPES.PHONE_CALL ? (cta?.phone ?? '') : '',
    copyText: type === CTA_TYPES.COPY_TEXT ? (cta?.copy_text ?? '') : '',
  };
};

/**
 * @param {object} [campaign] A campaign as serialized by the API.
 * @returns {{headline: string, body: string, imageUrl: string,
 *   imageAlt: string, themeColor: string, ctas: Array}}
 */
export const normalizeCampaignContent = (campaign = {}) => {
  const content = campaign.content ?? {};
  const ctas = Array.isArray(content.ctas)
    ? content.ctas.slice(0, MAX_CTAS).map(normalizeCta)
    : [];

  return {
    headline: content.headline ?? campaign.title ?? '',
    body: content.body ?? campaign.message ?? '',
    imageUrl: content.image_url ?? '',
    imageAlt: content.image_alt ?? '',
    themeColor: isValidThemeColor(content.theme_color)
      ? content.theme_color
      : '',
    ctas,
  };
};

/** A fresh CTA in the shape builder state carries. */
export const createCta = () => ({
  type: CTA_TYPES.SEND_MESSAGE,
  label: '',
  url: '',
  phone: '',
  copyText: '',
});

/**
 * Applies an edit to a CTA immutably, clearing per-type fields that the
 * (possibly new) type doesn't use so a type switch never leaks stale values
 * into the payload.
 */
export const applyCtaPatch = (cta, patch) => {
  const next = { ...cta, ...patch };
  if (!CTA_URL_TYPES.includes(next.type)) next.url = '';
  if (next.type !== CTA_TYPES.PHONE_CALL) next.phone = '';
  if (next.type !== CTA_TYPES.COPY_TEXT) next.copyText = '';
  return next;
};

const isBlank = value => !value || !String(value).trim();

/**
 * A CTA the backend will accept: it needs a label plus its per-type payload
 * field. CTAs a user is still filling in are kept in local state (and the
 * preview) but must not be persisted — the model rejects an incomplete CTA
 * outright.
 */
export const isPersistableCta = cta => {
  if (isBlank(cta.label)) return false;
  if (CTA_URL_TYPES.includes(cta.type)) return !isBlank(cta.url);
  if (cta.type === CTA_TYPES.PHONE_CALL) return !isBlank(cta.phone);
  if (cta.type === CTA_TYPES.COPY_TEXT) return !isBlank(cta.copyText);
  return true;
};

/**
 * Serializes builder state back into the snake_case `content` object the
 * update endpoint accepts. Fields irrelevant to a CTA's type carry null —
 * the model rejects, say, a url on a `phone_call`.
 */
export const buildContentPayload = ({
  headline,
  body,
  imageUrl,
  imageAlt,
  themeColor,
  ctas,
}) => ({
  headline: headline ?? '',
  body: body ?? '',
  image_url: imageUrl ?? '',
  image_alt: imageAlt ?? '',
  theme_color: isValidThemeColor(themeColor) ? themeColor : '',
  ctas: (ctas ?? [])
    .filter(isPersistableCta)
    .slice(0, MAX_CTAS)
    .map(({ type, label, url, phone, copyText }) => ({
      type,
      label,
      url: CTA_URL_TYPES.includes(type) ? url : null,
      phone: type === CTA_TYPES.PHONE_CALL ? phone : null,
      copy_text: type === CTA_TYPES.COPY_TEXT ? copyText : null,
    })),
});
